import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Loader2, Check } from 'lucide-react';

const HERO_BG = "https://customer-assets.emergentagent.com/job_gamma-to-web/artifacts/brgxn5xe_Grok%20Image%202026-02-05%20at%2017.41.51.png";

// Google Icon Component
const GoogleIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
);

const RegisterPage = () => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { register, loginWithGoogle, isAuthenticated, loading: authLoading } = useAuth();
    const navigate = useNavigate();

    // Redirect if already authenticated
    useEffect(() => {
        if (isAuthenticated && !authLoading) {
            navigate('/dashboard', { replace: true });
        }
    }, [isAuthenticated, authLoading, navigate]);

    const passwordRequirements = [
        { label: 'At least 6 characters', met: password.length >= 6 },
        { label: 'Passwords match', met: password === confirmPassword && password.length > 0 }
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        if (password.length < 6) {
            setError('Password must be at least 6 characters.');
            return;
        }

        setLoading(true);

        try {
            await register(email, password, name);
            navigate('/dashboard');
        } catch (err) {
            setError(err.message || 'Registration failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = () => {
        loginWithGoogle();
    };

    if (authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col items-center justify-center p-4 relative overflow-hidden">
            {/* Background image */}
            <div 
                className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: `url(${HERO_BG})` }}
            />
            {/* Dark overlay for readability */}
            <div className="absolute inset-0 bg-background/70 backdrop-blur-[2px]" />

            {/* Logo and title */}
            <div className="text-center mb-8 z-10 fade-in">
                <div className="flex items-center justify-center gap-3 mb-4">
                    <div className="p-3 rounded-lg bg-card border border-primary/30 shadow-gold">
                        <Eye className="w-8 h-8 text-primary" />
                    </div>
                </div>
                <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-2">
                    The Decrypted English Method
                </h1>
                <p className="font-body text-lg text-muted-foreground">
                    Begin Your Transformation
                </p>
            </div>

            {/* Register Card */}
            <Card className="w-full max-w-md z-10 bg-card/80 backdrop-blur-sm border-border/50 shadow-elevated fade-in" style={{ animationDelay: '0.1s' }}>
                <CardHeader className="text-center pb-4">
                    <CardTitle className="text-xl">Create Your Account</CardTitle>
                    <CardDescription className="font-body text-base">
                        Join the seekers of linguistic truth
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {error && (
                            <div className="p-3 rounded-md bg-destructive/10 border border-destructive/30 text-destructive text-sm font-ui">
                                {error}
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label htmlFor="name" className="font-ui text-sm text-foreground">
                                Full Name
                            </Label>
                            <Input
                                id="name"
                                type="text"
                                placeholder="Your name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                                autoComplete="name"
                                data-testid="register-name-input"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="email" className="font-ui text-sm text-foreground">
                                Email Address
                            </Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="your.email@domain.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                autoComplete="email"
                                data-testid="register-email-input"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password" className="font-ui text-sm text-foreground">
                                Password
                            </Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Create a password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    autoComplete="new-password"
                                    className="pr-10"
                                    data-testid="register-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    {showPassword ? (
                                        <EyeOff className="w-5 h-5" />
                                    ) : (
                                        <Eye className="w-5 h-5" />
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword" className="font-ui text-sm text-foreground">
                                Confirm Password
                            </Label>
                            <Input
                                id="confirmPassword"
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Confirm your password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                autoComplete="new-password"
                                data-testid="register-confirm-password-input"
                            />
                        </div>

                        {/* Password requirements */}
                        <div className="space-y-2">
                            {passwordRequirements.map((req, index) => (
                                <div
                                    key={index}
                                    className={`flex items-center gap-2 text-sm font-ui transition-colors ${
                                        req.met ? 'text-success' : 'text-muted-foreground'
                                    }`}
                                >
                                    <div className={`w-4 h-4 rounded-full flex items-center justify-center transition-all ${
                                        req.met ? 'bg-success/20' : 'bg-muted'
                                    }`}>
                                        {req.met && <Check className="w-3 h-3" />}
                                    </div>
                                    {req.label}
                                </div>
                            ))}
                        </div>

                        <Button
                            type="submit"
                            variant="gold"
                            size="lg"
                            className="w-full mt-2"
                            disabled={loading || !passwordRequirements.every(r => r.met)}
                            data-testid="register-submit-btn"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Creating Account...
                                </>
                            ) : (
                                'Begin Your Journey'
                            )}
                        </Button>

                        {/* Divider */}
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-border/50"></div>
                            </div>
                            <div className="relative flex justify-center text-xs uppercase">
                                <span className="bg-card px-2 text-muted-foreground font-ui">
                                    Or continue with
                                </span>
                            </div>
                        </div>

                        {/* Google Sign In Button */}
                        <Button
                            type="button"
                            data-testid="google-register-btn"
                            onClick={handleGoogleLogin}
                            variant="outline"
                            size="lg"
                            className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-50 text-gray-700 border-gray-300 hover:border-gray-400 transition-all"
                        >
                            <GoogleIcon className="w-5 h-5" />
                            <span className="font-medium">Continue with Google</span>
                        </Button>

                        <div className="text-center">
                            <p className="font-ui text-sm text-muted-foreground">
                                Already have an account?{' '}
                                <Link
                                    to="/login"
                                    className="text-primary hover:text-primary-glow transition-colors underline-offset-4 hover:underline"
                                    data-testid="login-link"
                                >
                                    Sign In
                                </Link>
                            </p>
                        </div>
                    </form>
                </CardContent>
            </Card>

            {/* Info notice */}
            <div className="mt-6 max-w-md z-10 text-center">
                <p className="text-sm text-muted-foreground font-ui px-4">
                    After registration, your account will need to be approved before you can access the course content.
                </p>
            </div>

            {/* Footer text */}
            <p className="mt-6 text-center text-sm text-muted-foreground font-ui z-10">
                &copy; {new Date().getFullYear()} Gringos English. All rights reserved.
            </p>
        </div>
    );
};

export default RegisterPage;
